import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { supabase } from '../../lib/supabaseClient';
import VerifiedBadge from '../verified/VerifiedBadge';
import { 
  ArrowLeft, 
  Heart, 
  Eye, 
  ShoppingCart, 
  Package, 
  Star, 
  CheckCircle, 
  Users, 
  TrendingUp,
  ShoppingBag,
  User as UserIcon,
  MessageCircle,
  Share2,
  Grid3x3,
  List,
  Shield,
  Truck,
  Tag,
  Home,
  Clock,
  Filter,
  ChevronDown,
  ChevronUp,
  MapPin,
  Mail,
  Phone,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Award,
  Calendar,
  DollarSign,
  Percent,
  Check,
  ThumbsUp,
  BarChart3,
  TrendingDown,
  Plug,
  PlugZap,
  MessageSquare,
  Copy,
  Zap,
  PackageOpen,
  Plus,
  Minus,
  Info,
  X,
  Trash2,
  Maximize2,
  Minimize2,
  RefreshCw,
  ExternalLink,
  EyeOff,
  Eye as EyeIcon,
  Repeat
} from 'lucide-react';
import './VendorShop.css';
import FollowVendor from './FollowVendor';
import ProductDisplay from '../market/ProductDisplay';

type ProductCondition = 'new' | 'like_new' | 'used_good' | 'used_fair' | 'for_parts' | string;

// ============================================================
// IMAGE TRANSFORM HELPERS
// ============================================================
const getThumbnailUrl = (originalUrl: string, width = 400, quality = 65): string => {
  if (!originalUrl) return originalUrl;
  if (!originalUrl.includes('/storage/v1/object/public/')) return originalUrl;
  const transformed = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const sep = transformed.includes('?') ? '&' : '?';
  return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
};

const getOriginalUrl = (url: string): string => {
  if (!url) return url;
  if (url.includes('/storage/v1/render/image/public/')) {
    const cleaned = url.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
    return cleaned.split('?')[0];
  }
  return url;
};
// ============================================================

// ============================================================
// URL NORMALIZATION — accepts any format
// ============================================================
const normalizeUrl = (
  raw: string | null | undefined,
  kind: 'website' | 'instagram' | 'facebook' | 'twitter'
): string | null => {
  if (!raw) return null;
  let v = String(raw).trim();
  if (!v) return null;

  // Strip common label prefixes the user might have typed
  v = v.replace(
    /^(website|site|web|url|link|instagram|insta|ig|facebook|fb|twitter|x)\s*[:\-]\s*/i,
    ''
  ).trim();

  // If it's an email, treat as website (mailto would be weird in iframe, use https on domain)
  const emailMatch = v.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
  if (emailMatch) {
    return `https://${emailMatch[1]}`;
  }

  // If it already has a protocol, return as-is (but fix common typos)
  if (/^https?:\/\//i.test(v)) {
    // Fix common protocol typos
    v = v.replace(/^htp:\/\//i, 'http://');
    v = v.replace(/^htps:\/\//i, 'https://');
    v = v.replace(/^http:\/\/https:\/\//i, 'https://');
    return v;
  }

  // Wrong-protocol variants like "htp://", "htps://", "http:/", "https:/"
  if (/^(htp|htps|http|https):?\/\/?/i.test(v)) {
    v = v.replace(/^(htp|htps|http|https):?\/\/?/i, '');
  }

  // Now v is a host or handle. Strip any leading slashes.
  v = v.replace(/^\/+/, '').trim();

  // If it looks like a domain (contains a dot), it's a website
  if (/^[^\s@]+\.[^\s@]{2,}(\/.*)?$/i.test(v)) {
    return `https://${v}`;
  }

  // Otherwise treat as a handle
  const handle = v.replace(/^@+/, '').trim();
  if (!handle) return null;

  switch (kind) {
    case 'instagram':
      return `https://instagram.com/${handle}`;
    case 'facebook':
      return `https://facebook.com/${handle}`;
    case 'twitter':
      return `https://twitter.com/${handle}`;
    case 'website':
    default:
      if (!handle.includes('.')) {
        return `https://${handle}.com`;
      }
      return `https://${handle}`;
  }
};
// ============================================================

// ============================================================
// INDEXEDDB CACHE (kept for writes only — reads are commented out)
// ============================================================
const VENDORSHOP_DB_NAME = 'gostorez_vendorshop_cache';
const VENDORSHOP_DB_VERSION = 1;
const VS_STORE_VENDORS = 'vendors';
const VS_STORE_PRODUCTS = 'products';
const VS_STORE_META = 'meta';

const openVendorShopDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(VENDORSHOP_DB_NAME, VENDORSHOP_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(VS_STORE_VENDORS)) {
          db.createObjectStore(VS_STORE_VENDORS, { keyPath: 'vendor_id' });
        }
        if (!db.objectStoreNames.contains(VS_STORE_PRODUCTS)) {
          db.createObjectStore(VS_STORE_PRODUCTS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(VS_STORE_META)) {
          db.createObjectStore(VS_STORE_META, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
};

const vsIdbClearStore = async (storeName: string): Promise<void> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { return; }
};

const vsIdbPut = async (storeName: string, item: any): Promise<void> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).put(item);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { return; }
};

const vsIdbPutMany = async (storeName: string, items: any[]): Promise<void> => {
  if (!items.length) return;
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      items.forEach(item => store.put(item));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { return; }
};

const vsIdbGet = async (storeName: string, key: string): Promise<any> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch { return null; }
};

const vsIdbGetAll = async (storeName: string): Promise<any[]> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch { return []; }
};

const vsIdbSetMeta = async (key: string, value: any): Promise<void> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VS_STORE_META, 'readwrite');
      tx.objectStore(VS_STORE_META).put({ key, value });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { return; }
};

const vsIdbGetMeta = async (key: string): Promise<any> => {
  try {
    const db = await openVendorShopDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VS_STORE_META, 'readonly');
      const req = tx.objectStore(VS_STORE_META).get(key);
      req.onsuccess = () => resolve(req.result?.value);
      req.onerror = () => reject(req.error);
    });
  } catch { return undefined; }
};
// ============================================================

interface Product {
  id: string;
  title: string;
  price: number;
  original_price?: number;
  currency: string;
  images: string[];
  description: string;
  detailed_description?: string;
  category: string;
  subcategory?: string;
  condition: ProductCondition;
  vendor_id: string;
  vendor_name: string;
  vendor_logo?: string;
  views_count: number;
  likes_count: number;
  sales_count: number;
  inventory: number;
  is_active: boolean;
  is_promoted: boolean;
  created_at: string;
  updated_at: string;
  tags: string[];
  specifications?: Record<string, string>;
  shipping_info?: any;
  warranty_info?: any;
  delivery_campus_ids?: string[];
  user_id: string;
}

interface VendorProfile {
  id: string;
  vendor_id: string;
  shop_name: string;
  profile_image: string;
  cover_image?: string;
  rating: number;
  followers_count: number;
  completed_trades: number;
  is_verified: boolean;
  online_status: boolean;
  description: string;
  created_at: string;
  user_id: string;
  response_time?: string;
  response_rate?: number;
  delivery_campus_ids?: string[];
  contact_email?: string;
  contact_phone?: string;
  social_media?: any;
  policies?: any;
  bio?: string;
  location?: string;
  business_hours?: string;
  average_rating?: number;
  rating_count?: number;
  total_products?: number;
  total_sales?: number;
  logo_url?: string;
  return_policy?: string;
  shipping_policy?: string;
  total_views?: number;
  total_likes?: number;
  total_revenue?: number;
  business_email?: string;
  instagram_handle?: string;
  facebook_page?: string;
  twitter_handle?: string;
  website?: string;
  sales_count?: number;
}

interface Review {
  id: string;
  product_id: string;
  user_id: string;
  user_name: string;
  rating: number;
  review_text: string;
  created_at: string;
  helpful_count: number;
  vendor_id: string;
}

interface VendorStat {
  total_products: number;
  total_sales: number;
  total_views: number;
  total_likes: number;
  total_revenue: number;
  average_rating: number;
  followers_count: number;
  response_rate: number;
  response_time: string;
}

interface VendorShopProps {
  vendorId?: string;
  embedded?: boolean;
  onClose?: () => void;
  onProductClick?: (product: Product) => void;
  initialProductId?: string | null;
  isBlurred?: boolean;
  onToggleExpand?: () => void;
}

interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  price: number;
}

interface ShopLink {
  key: string;
  label: string;
  url: string;
  icon: React.ReactNode;
  type: 'website' | 'instagram' | 'facebook' | 'twitter';
}

const VendorShop: React.FC<VendorShopProps> = ({
  vendorId: propVendorId,
  embedded = false,
  onClose,
  onProductClick,
  initialProductId = null,
  isBlurred = false,
  onToggleExpand
}) => {
  const params = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const vendorId = propVendorId || params.vendorId;
  
  const [vendor, setVendor] = useState<VendorProfile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<VendorStat | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'reviews' | 'about' | 'policies'>('products');
  const [productFilter, setProductFilter] = useState<'all' | 'available' | 'promoted'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'price_low' | 'price_high' | 'popular'>('newest');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [cartStatus, setCartStatus] = useState<{ [key: string]: boolean }>({});
  const [favoritesStatus, setFavoritesStatus] = useState<{ [key: string]: boolean }>({});
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [dataLoadAttempts, setDataLoadAttempts] = useState<number>(0);
  const [vendorLoadError, setVendorLoadError] = useState<string | null>(null);
  const [productsLoadError, setProductsLoadError] = useState<string | null>(null);
  const [reviewsLoadError, setReviewsLoadError] = useState<string | null>(null);
  const [statsLoadError, setStatsLoadError] = useState<string | null>(null);
  
  const [localSelectedProduct, setLocalSelectedProduct] = useState<Product | null>(null);
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCartTray, setShowCartTray] = useState<boolean>(false);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState<boolean>(false);

  const [showReviewStars, setShowReviewStars] = useState<boolean>(false);
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [showReviewComment, setShowReviewComment] = useState<boolean>(false);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // ============================================================
  // INFO TAB LINK VIEWER STATE
  // ============================================================
  const [currentLinkIndex, setCurrentLinkIndex] = useState<number>(0);
  const [iframeKey, setIframeKey] = useState<number>(0);
  const [isLinkFullscreen, setIsLinkFullscreen] = useState<boolean>(false);
  const [showLinkControls, setShowLinkControls] = useState<boolean>(true);
  const [hoverFullscreen, setHoverFullscreen] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // ============================================================

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (vendorId) {
      loadAllVendorData();
    } else {
      setError('No vendor ID provided');
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    applyProductFiltersAndSorting();
  }, [products, productFilter, sortBy, viewMode]);

  useEffect(() => {
    const savedCart = localStorage.getItem('vendor_shop_cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('vendor_shop_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Reset link viewer when vendor changes
  useEffect(() => {
    setCurrentLinkIndex(0);
    setIframeKey(0);
    setIsLinkFullscreen(false);
    setShowLinkControls(true);
  }, [vendorId]);

  // ============================================================
  // VENDOR CONTACT FALLBACK
  // ============================================================
  const ensureVendorContact = async (vendorData: any): Promise<any> => {
    const needsPhone = !vendorData.contact_phone;
    const needsEmail = !vendorData.business_email;

    if (!needsPhone && !needsEmail) return vendorData;

    try {
      if (!vendorData.user_id) return vendorData;

      const { data: userData } = await supabase
        .from('users')
        .select('phone, email')
        .eq('firebase_uid', vendorData.user_id)
        .maybeSingle();

      if (!userData) return vendorData;

      const patch: any = {};
      if (needsPhone && userData.phone) patch.contact_phone = userData.phone;
      if (needsEmail && userData.email) patch.business_email = userData.email;

      if (Object.keys(patch).length === 0) return vendorData;

      await supabase
        .from('vendor_profiles')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('vendor_id', vendorData.vendor_id);

      return { ...vendorData, ...patch };
    } catch (err) {
      console.warn('Could not backfill vendor contact:', err);
      return vendorData;
    }
  };
  // ============================================================

  // ============================================================
  // CACHE LOADING — COMMENTED OUT (aggressive caching disabled)
  // ============================================================
  /*
  const loadFromCache = async (): Promise<boolean> => {
    if (!vendorId) return false;
    try {
      const cachedVendor = await vsIdbGet(VS_STORE_VENDORS, vendorId);
      if (!cachedVendor) return false;

      const allProducts = await vsIdbGetAll(VS_STORE_PRODUCTS);
      const cachedProducts = allProducts.filter((p: any) => p.vendor_id === vendorId);
      const cachedReviews = await vsIdbGet('meta', `reviews_${vendorId}`);

      // ... (cache transform code)
      return true;
    } catch (err) {
      console.warn('Cache load failed:', err);
      return false;
    }
  };
  */
  // ============================================================

  const loadAllVendorData = async () => {
    setLoading(true);
    setError(null);
    setVendorLoadError(null);
    setProductsLoadError(null);
    setReviewsLoadError(null);
    setStatsLoadError(null);
    setDataLoadAttempts(prev => prev + 1);

    if (!vendorId) {
      setError('Vendor ID is required');
      setLoading(false);
      return;
    }

    // ⚡ Aggressive caching DISABLED — always fetch fresh
    try {
      await Promise.all([
        loadVendorProfileData(),
        loadVendorProductsData(),
        loadVendorReviewsData()
      ]);
      calculateVendorStatistics();
      if (currentUser) fetchCartAndFavoritesStatus();
      // await vsIdbSetMeta(`lastFetched_${vendorId}`, new Date().toISOString());
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load vendor shop data');
    } finally {
      setLoading(false);
    }
  };

  const loadVendorProfileData = async () => {
    try {
      const { data: vendorData, error: vendorError } = await supabase
        .from('vendor_profiles')
        .select('*')
        .eq('vendor_id', vendorId)
        .single();

      if (vendorError || !vendorData) {
        setVendorLoadError(vendorError?.message || 'Vendor not found');
        throw new Error('Vendor not found');
      }

      const enrichedVendor = await ensureVendorContact(vendorData);

      const transformedVendor: VendorProfile = {
        ...enrichedVendor,
        shop_name: enrichedVendor.shop_name || 'Unnamed Shop',
        profile_image: enrichedVendor.profile_image || enrichedVendor.logo_url || '',
        cover_image: enrichedVendor.cover_image,
        rating: enrichedVendor.average_rating || 0,
        followers_count: enrichedVendor.followers_count || 0,
        completed_trades: enrichedVendor.sales_count || enrichedVendor.total_sales || 0,
        is_verified: false,
        online_status: true,
        description: enrichedVendor.bio || 'This vendor has not provided a shop description yet.',
        user_id: enrichedVendor.user_id || '',
        response_time: 'Within 24 hours',
        response_rate: 95,
        delivery_campus_ids: [],
        contact_email: enrichedVendor.business_email,
        contact_phone: enrichedVendor.contact_phone,
        social_media: {
          instagram: enrichedVendor.instagram_handle,
          facebook: enrichedVendor.facebook_page,
          twitter: enrichedVendor.twitter_handle,
          website: enrichedVendor.website
        },
        policies: {
          return_policy: enrichedVendor.return_policy,
          shipping_policy: enrichedVendor.shipping_policy,
          payment_methods: ['Cash on Delivery', 'Bank Transfer', 'Card Payment']
        },
        bio: enrichedVendor.bio,
        location: enrichedVendor.location,
        business_hours: enrichedVendor.business_hours,
        average_rating: enrichedVendor.average_rating,
        rating_count: enrichedVendor.rating_count,
        total_products: enrichedVendor.total_products,
        total_sales: enrichedVendor.total_sales,
        logo_url: enrichedVendor.logo_url,
        return_policy: enrichedVendor.return_policy,
        shipping_policy: enrichedVendor.shipping_policy,
        total_views: enrichedVendor.total_views || 0,
        total_likes: enrichedVendor.total_likes || 0,
        total_revenue: enrichedVendor.total_revenue || 0,
        business_email: enrichedVendor.business_email,
        instagram_handle: enrichedVendor.instagram_handle,
        facebook_page: enrichedVendor.facebook_page,
        twitter_handle: enrichedVendor.twitter_handle,
        website: enrichedVendor.website,
        sales_count: enrichedVendor.sales_count || enrichedVendor.total_sales || 0
      };

      console.log('[VendorShop] Vendor loaded:', {
        shop_name: transformedVendor.shop_name,
        website: transformedVendor.website,
        instagram_handle: transformedVendor.instagram_handle,
        facebook_page: transformedVendor.facebook_page,
        twitter_handle: transformedVendor.twitter_handle,
      });

      setVendor(transformedVendor);

      // Cache write only (no read gate)
      await vsIdbPut(VS_STORE_VENDORS, transformedVendor);
    } catch (err: any) {
      console.error('Exception loading vendor profile:', err);
      throw err;
    }
  };

  const calculateAndUpdateVendorStats = async () => {
    try {
      const { data: productsData } = await supabase
        .from('products')
        .select('views_count, likes_count, sales_count, price, inventory, is_active')
        .eq('vendor_id', vendorId);

      const totalProducts = productsData?.length || 0;
      const totalSales = productsData?.reduce((sum, product) => sum + (product.sales_count || 0), 0) || 0;
      const totalViews = productsData?.reduce((sum, product) => sum + (product.views_count || 0), 0) || 0;
      const totalLikes = productsData?.reduce((sum, product) => sum + (product.likes_count || 0), 0) || 0;
      
      const totalRevenue = productsData?.reduce((sum, product) => {
        return sum + (product.price * (product.sales_count || 0));
      }, 0) || 0;

      const activeProducts = productsData?.filter(product => product.is_active).length || 0;

      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('vendor_id', vendorId);

      const averageRating = reviewsData && reviewsData.length > 0 
        ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / reviewsData.length
        : 0;

      await supabase
        .from('vendor_profiles')
        .update({
          total_products: activeProducts,
          total_sales: totalSales,
          total_views: totalViews,
          total_likes: totalLikes,
          total_revenue: totalRevenue,
          average_rating: averageRating,
          rating_count: reviewsData?.length || 0,
          updated_at: new Date().toISOString()
        })
        .eq('vendor_id', vendorId);
    } catch (err) {
      console.error('Exception calculating vendor stats:', err);
    }
  };

  const loadVendorProductsData = async () => {
    try {
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('vendor_id', vendorId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (productsError) {
        setProductsLoadError(productsError.message);
        setProducts([]);
      } else {
        const transformedProducts: Product[] = (productsData || []).map(product => ({
          ...product,
          original_price: product.original_price || product.price,
          detailed_description: product.description,
          subcategory: '',
          condition: (product.condition as ProductCondition) || 'new',
          updated_at: product.updated_at || product.created_at || new Date().toISOString(),
          tags: product.tags || [],
          specifications: {},
          shipping_info: {},
          warranty_info: undefined,
          delivery_campus_ids: [],
          vendor_name: product.vendor_name || '',
          vendor_logo: product.vendor_logo,
          views_count: product.views_count || 0,
          likes_count: product.likes_count || 0,
          sales_count: product.sales_count || 0,
          inventory: product.inventory || 0,
          is_promoted: product.is_promoted || false
        }));

        setProducts(transformedProducts);

        await vsIdbPutMany(VS_STORE_PRODUCTS, transformedProducts);
      }
    } catch (err: any) {
      console.error('Exception loading products:', err);
      setProducts([]);
    }
  };

  const loadVendorReviewsData = async () => {
    try {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (reviewsError) {
        setReviewsLoadError(reviewsError.message);
        setReviews([]);
      } else {
        const transformedReviews: Review[] = (reviewsData || []).map(review => ({
          ...review,
          user_name: review.user_name || 'Customer',
          helpful_count: review.helpful_count || 0,
          vendor_id: review.vendor_id || vendorId || ''
        }));

        setReviews(transformedReviews);

        await vsIdbSetMeta(`reviews_${vendorId}`, transformedReviews);
      }
    } catch (err: any) {
      console.error('Exception loading reviews:', err);
      setReviews([]);
    }
  };

  const calculateVendorStatistics = () => {
    try {
      const totalProducts = products.length || 0;
      const totalSales = products.reduce((sum, product) => sum + (product.sales_count || 0), 0);
      const totalViews = products.reduce((sum, product) => sum + (product.views_count || 0), 0);
      const totalLikes = products.reduce((sum, product) => sum + (product.likes_count || 0), 0);
      const followersCount = vendor?.followers_count || 0;
      
      const totalRevenue = products.reduce((sum, product) => {
        return sum + (product.price * (product.sales_count || 0));
      }, 0);

      const averageRating = reviews.length > 0 
        ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
        : vendor?.average_rating || 0;

      const calculatedStats: VendorStat = {
        total_products: totalProducts,
        total_sales: totalSales,
        total_views: totalViews,
        total_likes: totalLikes,
        total_revenue: totalRevenue,
        followers_count: followersCount,
        average_rating: parseFloat(averageRating.toFixed(1)),
        response_rate: 95,
        response_time: 'Within 24 hours'
      };

      setStats(calculatedStats);
    } catch (err) {
      console.error('Error calculating statistics:', err);
      setStatsLoadError('Failed to calculate statistics');
    }
  };

  const fetchCartAndFavoritesStatus = () => {
    if (!currentUser || products.length === 0) return;
    fetchCartStatus(currentUser.uid, products.map(p => p.id));
    fetchFavoritesStatus(currentUser.uid, products.map(p => p.id));
  };

  const applyProductFiltersAndSorting = () => {
    let filtered = [...products];

    switch (productFilter) {
      case 'available':
        filtered = filtered.filter(product => product.inventory > 0);
        break;
      case 'promoted':
        filtered = filtered.filter(product => product.is_promoted);
        break;
      default:
        break;
    }

    switch (sortBy) {
      case 'price_low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'popular':
        filtered.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
    }

    setFilteredProducts(filtered);
  };

  const fetchCartStatus = async (userId: string, productIds: string[]) => {
    try {
      if (productIds.length === 0) return;
      
      const { data, error } = await supabase
        .from('carts')
        .select('product_id')
        .eq('user_id', userId)
        .in('product_id', productIds);

      if (error) return;

      const statusMap: { [key: string]: boolean } = {};
      data?.forEach(item => {
        statusMap[item.product_id] = true;
      });

      setCartStatus(statusMap);
    } catch (error) {
      console.error('Exception in fetchCartStatus:', error);
    }
  };

  const fetchFavoritesStatus = async (userId: string, productIds: string[]) => {
    try {
      if (productIds.length === 0) return;
      
      const { data, error } = await supabase
        .from('user_favorites')
        .select('product_id')
        .eq('user_id', userId)
        .in('product_id', productIds);

      if (error) return;

      const statusMap: { [key: string]: boolean } = {};
      data?.forEach(item => {
        statusMap[item.product_id] = true;
      });

      setFavoritesStatus(statusMap);
    } catch (error) {
      console.error('Exception in fetchFavoritesStatus:', error);
    }
  };

  const handleProductClick = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (onProductClick) {
      onProductClick(product);
    } else {
      setLocalSelectedProduct(product);
    }
    
    try {
      supabase.rpc('increment_views', { product_id: product.id });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const handleContactVendor = () => {
    if (!vendor) return;
    
    const phoneNumber = vendor.contact_phone || '';
    if (!phoneNumber) {
      alert('Vendor phone number not available');
      return;
    }
    
    const formattedPhone = phoneNumber.replace(/\D/g, '');
    const message = `Hello ${vendor.shop_name}, from Gostorez I would like to inquire about your products.`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareShop = async () => {
    if (!vendor) return;
    
    try {
      const shareData = {
        title: `${vendor.shop_name} Shop`,
        text: `Check out ${vendor.shop_name} on GOSTOREz!`,
        url: `${window.location.origin}/vendor/${vendor.vendor_id}`,
      };
      
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert('📋 Shop link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing shop:', error);
    }
  };

  const addToCart = (product: Product) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.map(item => 
          item.productId === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        productId: product.id,
        productName: product.title,
        quantity: 1,
        price: product.price
      }];
    });
    setShowCartTray(true);
  };

  const removeFromCart = (productId: string) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.productId === productId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => 
          item.productId === productId 
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(item => item.productId !== productId);
    });
  };

  const clearCart = () => {
    setCartItems([]);
    setShowCartTray(false);
    localStorage.removeItem('vendor_shop_cart');
  };

  const getProductQuantity = (productId: string): number => {
    const item = cartItems.find(item => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const getTotalItems = (): number => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalPrice = (): number => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleWhatsAppOrder = () => {
    if (!vendor) return;
    
    const phoneNumber = vendor.contact_phone || '';
    if (!phoneNumber) {
      alert('Vendor phone number not available');
      return;
    }
    
    let orderSummary = `🛒 Order from ${vendor.shop_name}\n\n`;
    cartItems.forEach(item => {
      orderSummary += `${item.productName} x${item.quantity} = ${formatPrice(item.price * item.quantity, 'NGN')}\n`;
    });
    orderSummary += `\nTotal: ${formatPrice(getTotalPrice(), 'NGN')}`;
    orderSummary += `\n\nPlease confirm my order.`;
    
    const formattedPhone = phoneNumber.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(orderSummary)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleToggleFavorite = async (product: Product) => {
    if (!currentUser) {
      navigate('/signin', { state: { from: `/vendor/${vendorId}` } });
      return;
    }

    try {
      const isFavorite = favoritesStatus[product.id];
      
      if (isFavorite) {
        await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', currentUser.uid)
          .eq('product_id', product.id);

        setFavoritesStatus(prev => ({ ...prev, [product.id]: false }));
        setProducts(prev => prev.map(p => 
          p.id === product.id 
            ? { ...p, likes_count: Math.max(0, p.likes_count - 1) }
            : p
        ));
      } else {
        await supabase
          .from('user_favorites')
          .insert({
            user_id: currentUser.uid,
            product_id: product.id,
            vendor_id: product.vendor_id,
            created_at: new Date().toISOString()
          });

        setFavoritesStatus(prev => ({ ...prev, [product.id]: true }));
        setProducts(prev => prev.map(p => 
          p.id === product.id 
            ? { ...p, likes_count: p.likes_count + 1 }
            : p
        ));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleBack = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const formatPrice = (price: number, currency: string = 'NGN') => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<Star key={i} size={12} fill="#f59e0b" stroke="#f59e0b" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<Star key={i} size={12} fill="#f59e0b" stroke="#f59e0b" />);
      } else {
        stars.push(<Star key={i} size={12} stroke="#d1d5db" />);
      }
    }
    return stars;
  };

  const handleFollowChange = (following: boolean) => {
    if (vendor) {
      setVendor(prev => prev ? {
        ...prev,
        followers_count: following ? prev.followers_count + 1 : Math.max(0, prev.followers_count - 1)
      } : null);
    }
  };

  const handleRetryLoad = () => {
    setHasLoadedFromCache(false);
    loadAllVendorData();
  };

  const handleWhatsAppChat = () => {
    if (!vendor) return;
    const phoneNumber = vendor.contact_phone || '';
    if (!phoneNumber) {
      alert('Vendor phone number not available');
      return;
    }
    const formattedPhone = phoneNumber.replace(/\D/g, '');
    const message = `Hello ${vendor.shop_name}, I would like to inquire about your products.`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppLocationClick = () => {
    handleWhatsAppChat();
  };

  const handleAddReviewClick = () => {
    setShowReviewStars(true);
    setSelectedRating(0);
    setShowReviewComment(false);
    setReviewComment('');
    setReviewError(null);
  };

  const handleStarSelect = (rating: number) => {
    setSelectedRating(rating);
    setShowReviewComment(true);
  };

  const handleSubmitReview = async () => {
    if (selectedRating === 0) {
      setReviewError('Please select a rating');
      return;
    }
    if (!reviewComment.trim()) {
      setReviewError('Please write a comment');
      return;
    }

    setIsSubmittingReview(true);
    setReviewError(null);

    try {
      const userId = currentUser?.uid || 'anonymous';
      const userName = currentUser?.displayName || currentUser?.email || 'Guest';

      const { error } = await supabase
        .from('reviews')
        .insert({
          vendor_id: vendor?.vendor_id,
          user_id: userId,
          user_name: userName,
          rating: selectedRating,
          review_text: reviewComment.trim(),
          created_at: new Date().toISOString(),
          helpful_count: 0
        });

      if (error) {
        setReviewError('Failed to submit review. Please try again.');
        return;
      }

      alert('Thank you for your review!');
      
      setShowReviewStars(false);
      setShowReviewComment(false);
      setSelectedRating(0);
      setReviewComment('');
      setReviewError(null);
      await loadVendorReviewsData();
      calculateVendorStatistics();
    } catch (error) {
      setReviewError('Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleHelpfulClick = async (reviewId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('increment_helpful', { review_id: reviewId });

      if (error) return;

      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, helpful_count: data }
          : review
      ));
    } catch (error) {
      console.error('Error in handleHelpfulClick:', error);
    }
  };

  const timeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ============================================================
  // INFO TAB LINK VIEWER LOGIC
  // ============================================================
  const shopLinks = useMemo<ShopLink[]>(() => {
    if (!vendor) {
      console.log('[VendorShop] shopLinks: no vendor yet');
      return [];
    }
    const links: ShopLink[] = [];

    const websiteUrl = normalizeUrl(vendor.website, 'website');
    const instaUrl = normalizeUrl(vendor.instagram_handle, 'instagram');
    const fbUrl = normalizeUrl(vendor.facebook_page, 'facebook');
    const twUrl = normalizeUrl(vendor.twitter_handle, 'twitter');

    console.log('[VendorShop] Raw links:', {
      website: vendor.website,
      instagram_handle: vendor.instagram_handle,
      facebook_page: vendor.facebook_page,
      twitter_handle: vendor.twitter_handle,
    });
    console.log('[VendorShop] Normalized URLs:', {
      websiteUrl,
      instaUrl,
      fbUrl,
      twUrl,
    });

    if (websiteUrl) {
      links.push({
        key: 'website',
        label: 'Website',
        url: websiteUrl,
        icon: <Globe size={12} />,
        type: 'website'
      });
    }
    if (instaUrl) {
      links.push({
        key: 'instagram',
        label: 'Instagram',
        url: instaUrl,
        icon: <Instagram size={12} />,
        type: 'instagram'
      });
    }
    if (fbUrl) {
      links.push({
        key: 'facebook',
        label: 'Facebook',
        url: fbUrl,
        icon: <Facebook size={12} />,
        type: 'facebook'
      });
    }
    if (twUrl) {
      links.push({
        key: 'twitter',
        label: 'Twitter',
        url: twUrl,
        icon: <Twitter size={12} />,
        type: 'twitter'
      });
    }

    console.log('[VendorShop] Final shopLinks:', links);
    return links;
  }, [vendor]);

  const currentLink = shopLinks[currentLinkIndex] || null;
  const hasMultipleLinks = shopLinks.length > 1;

  const handleSwitchLink = () => {
    if (!hasMultipleLinks) return;
    setCurrentLinkIndex(prev => (prev + 1) % shopLinks.length);
    setIframeKey(prev => prev + 1);
  };

  const handleRefreshLink = () => {
    setIframeKey(prev => prev + 1);
  };

  const handleOpenInBrowser = () => {
    if (currentLink) {
      window.open(currentLink.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleFullscreen = () => {
    setIsLinkFullscreen(prev => !prev);
  };

  const handleHideControls = () => {
    setShowLinkControls(false);
    setHoverFullscreen(false);
  };

  const handleShowControls = () => {
    setShowLinkControls(true);
  };
  // ============================================================

  if (loading && !vendor) {
    return (
      <div className="vendorshop-loading-container">
        <div className="vendorshop-loading-spinner-container">
          <div className="vendorshop-loading-spinner"></div>
        </div>
        <div className="vendorshop-loading-text-container">
          <p className="vendorshop-loading-text">Loading Online Shop...</p>
        </div>
        <div className="vendorshop-loading-details">
          <p className="vendorshop-loading-detail">🛍️ Fetching Resources Please Wait</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="vendorshop-error-container">
        <div className="vendorshop-error-icon-container">
          <div className="vendorshop-error-icon">⚠️</div>
        </div>
        <div className="vendorshop-error-text-container">
          <h3 className="vendorshop-error-title">Shop Not Found</h3>
          <p className="vendorshop-error-message">
            {error || 'This vendor shop does not exist or has been removed.'}
          </p>
          <div className="vendorshop-error-details">
            📡 Check Internet Connection and Try again.
          </div>
          <div className="vendorshop-error-actions">
            <button 
              className="vendorshop-error-retry-button"
              onClick={handleRetryLoad}
            >
              Try again
            </button>
            <button 
              className="vendorshop-error-back-button"
              onClick={() => navigate('/market')}
            >
              Back to Market
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalViews = products.reduce((sum, product) => sum + (product.views_count || 0), 0);
  const totalReviews = reviews.length || vendor.rating_count || 0;
  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : vendor.average_rating || 0;

  const coverThumb = vendor.cover_image
    ? getThumbnailUrl(vendor.cover_image, 800, 60)
    : '';
  const logoThumb = (vendor.logo_url || vendor.profile_image)
    ? getThumbnailUrl(vendor.logo_url || vendor.profile_image, 120, 70)
    : '';

  return (
    <div className={`vendorshop-main-container ${embedded ? 'vendorshop-main-container-embedded' : ''} ${isBlurred ? 'vendorshop-main-container-blurred' : ''}`}>
      
      {showCartTray && cartItems.length > 0 && (
        <div className="vendorshop-cart-tray">
          <div className="vendorshop-cart-tray-header">
            <span className="vendorshop-cart-tray-title">🛒 ({getTotalItems()} items)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button 
                className="vendorshop-cart-tray-clear"
                onClick={clearCart}
                title="Clear cart"
                style={{
                  background: 'rgba(239, 68, 68, 0)',
                  border: 'none',
                  color: '#ef4444',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '11px',
                  fontWeight: 600
                }}
              >
                <Trash2 size={12} />
              </button>
              <button 
                className="vendorshop-cart-tray-close"
                onClick={() => setShowCartTray(false)}
              >
                ✕
              </button>
            </div>
          </div>
          <div className="vendorshop-cart-tray-items">
            {cartItems.map(item => (
              <div key={item.productId} className="vendorshop-cart-tray-item">
                <span className="vendorshop-cart-tray-item-name">{item.productName}</span>
                <span className="vendorshop-cart-tray-item-qty">x{item.quantity}</span>
                <span className="vendorshop-cart-tray-item-price">{formatPrice(item.price * item.quantity, 'NGN')}</span>
              </div>
            ))}
          </div>
          <div className="vendorshop-cart-tray-total">
            <span>Total: {formatPrice(getTotalPrice(), 'NGN')}</span>
          </div>
          <button 
            className="vendorshop-cart-tray-whatsapp"
            onClick={handleWhatsAppOrder}
          >
            <MessageCircle size={16} /> Inquire 
          </button>
        </div>
      )}
      
      <div className="vendorshop-cover-section-main">
        {coverThumb ? (
          <div 
            className="vendorshop-cover-image-background-main"
            style={{ backgroundImage: `url(${coverThumb})` }}
          />
        ) : (
          <div className="vendorshop-cover-placeholder-main">
            <ShoppingBag size={48} className="vendorshop-cover-placeholder-icon" />
            <span className="vendorshop-cover-placeholder-text">{vendor.shop_name}</span>
          </div>
        )}
        
        <div className="vendorshop-shop-profile-overlay-main">
          <div className="vendorshop-shop-profile-card-main">
            <div className="vendorshop-shop-profile-logo-section-main">
              {logoThumb ? (
                <img 
                  src={logoThumb} 
                  alt={vendor.shop_name}
                  className="vendorshop-shop-profile-logo-main"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.dataset.fallback === 'true') return;
                    target.dataset.fallback = 'true';
                    const original = vendor.logo_url || vendor.profile_image;
                    if (original) target.src = getOriginalUrl(original);
                  }}
                />
              ) : (
                <div className="vendorshop-shop-profile-logo-placeholder-main">
                  {vendor.shop_name.charAt(0).toUpperCase()}
                </div>
              )}
              
              <div className="vendorshop-shop-profile-info-main">
               <h1 className="vendorshop-shop-profile-name-main">
  {vendor.shop_name}
 
</h1>
                <div className="vendorshop-shop-profile-rating-main">
                  <div className="vendorshop-shop-profile-stars-main">
                    {renderStars(averageRating)}
                  </div>
                </div>
                {vendor.location ? (
                  <div className="vendorshop-shop-profile-location-main" onClick={handleWhatsAppLocationClick} style={{ cursor: 'pointer' }}>
                    <MapPin size={15} className="vendorshop-shop-profile-location-icon" />
                    <span className="vendorshop-shop-profile-location-text">{vendor.location}</span>
                  </div>
                ) : (
                  <div className="vendorshop-shop-profile-location-main" onClick={handleWhatsAppLocationClick} style={{ cursor: 'pointer' }}>
                    <MessageCircle size={15} className="vendorshop-shop-profile-location-icon" />
                    <span className="vendorshop-shop-profile-location-text">WhatsApp</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="vendorshop-shop-profile-actions-main">
              <FollowVendor 
                vendorId={vendor.vendor_id}
                vendorName={vendor.shop_name}
                onFollowChange={handleFollowChange}
                className="vendorshop-follow-vendor-button-main-class"
              />
              
              <button 
                className="vendorshop-chat-vendor-button-main"
                onClick={handleWhatsAppChat}
                title="Chat on WhatsApp"
              >
                <MessageCircle size={12} className="vendorshop-chat-vendor-button-icon" />
                <span className="vendorshop-chat-vendor-button-text"></span>
              </button>
              
              <button 
                className="vendorshop-share-shop-button-main"
                onClick={handleShareShop}
                title="Share this shop"
              >
                <Share2 size={12} className="vendorshop-share-shop-button-icon" />
              </button>
            </div>
          </div>
          
          <div className="vendorshop-shop-stats-overlay-main">

             {vendorId && <VerifiedBadge shopId={vendorId} variant="badge" />}
             
            <div className="vendorshop-shop-stat-item-main">
              <Users size={10} className="vendorshop-shop-stat-icon-main" />
              <div className="vendorshop-shop-stat-content-main">
                <div className="vendorshop-shop-stat-value-main">{stats?.followers_count || vendor.followers_count || 0}</div>
              </div>
            </div>
            
            <div className="vendorshop-shop-stat-item-main">
              <Star size={10} className="vendorshop-shop-stat-icon-main" />
              <div className="vendorshop-shop-stat-content-main">
                <div className="vendorshop-shop-stat-value-main">{averageRating.toFixed(1)}</div>
              </div>
            </div>
            
            <div className="vendorshop-shop-stat-item-main">
              <Eye size={10} className="vendorshop-shop-stat-icon-main" />
              <div className="vendorshop-shop-stat-content-main">
                <div className="vendorshop-shop-stat-value-main">{totalViews}</div>
              </div>
            </div>
            
            <div className="vendorshop-shop-stat-item-main">
              <MessageCircle size={10} className="vendorshop-shop-stat-icon-main" />
              <div className="vendorshop-shop-stat-content-main">
                <div className="vendorshop-shop-stat-value-main">{totalReviews}</div>
              </div>
            </div> 
          </div>
        </div>
      </div>

      <nav className="vendorshop-tabs-navigation-main">
        <button 
          className={`vendorshop-tab-button-main ${activeTab === 'products' ? 'vendorshop-tab-button-main-active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          <Grid3x3 size={10} className="vendorshop-tab-icon-main" />
          <span className="vendorshop-tab-text-main">Products</span>
          {products.length > 0 && (
            <span className="vendorshop-tab-badge-main">{products.length}</span>
          )}
        </button>
        
        <button 
          className={`vendorshop-tab-button-main ${activeTab === 'reviews' ? 'vendorshop-tab-button-main-active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          <Star size={10} className="vendorshop-tab-icon-main" />
          <span className="vendorshop-tab-text-main">Reviews</span>
          {reviews.length > 0 && (
            <span className="vendorshop-tab-badge-main">{reviews.length}</span>
          )}
        </button>
        
        <button 
          className={`vendorshop-tab-button-main ${activeTab === 'about' ? 'vendorshop-tab-button-main-active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          <Home size={10} className="vendorshop-tab-icon-main" />
          <span className="vendorshop-tab-text-main">About</span>
        </button>
        
        <button 
          className={`vendorshop-tab-button-main ${activeTab === 'policies' ? 'vendorshop-tab-button-main-active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          <Info size={10} className="vendorshop-tab-icon-main" />
          <span className="vendorshop-tab-text-main">Info</span>
        </button>
      </nav>

      <main className="vendorshop-content-area-main">
        {activeTab === 'products' && (
          <div className="vendorshop-products-section-main">
            <div className="vendorshop-products-controls-section-main">
              
              <div className="vendorshop-products-controls-left-section-main">
                <div className="vendorshop-view-mode-toggle-section-main">
                  <button 
                    className={`vendorshop-view-mode-button-main ${viewMode === 'grid' ? 'vendorshop-view-mode-button-main-active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <Grid3x3 size={10} className="vendorshop-view-mode-icon-main" />
                  </button>
                  <button 
                    className={`vendorshop-view-mode-button-main ${viewMode === 'list' ? 'vendorshop-view-mode-button-main-active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <List size={10} className="vendorshop-view-mode-icon-main" />
                  </button>
                </div>
                
                <div className="vendorshop-social-filter-section-main">
                  {vendor.contact_phone && (
                    <button 
                      className="vendorshop-social-filter-button whatsapp"
                      onClick={handleWhatsAppChat}
                      title="Chat on WhatsApp"
                    >
                      <MessageCircle size={14} />
                    </button>
                  )}
                  
                  {vendor.instagram_handle && (
                    <button 
                      className="vendorshop-social-filter-button instagram"
                      onClick={() => window.open(`https://instagram.com/${vendor.instagram_handle}`, '_blank')}
                      title="Instagram"
                    >
                      <Instagram size={14} />
                    </button>
                  )}
                  
                  {vendor.facebook_page && (
                    <button 
                      className="vendorshop-social-filter-button facebook"
                      onClick={() => window.open(`https://facebook.com/${vendor.facebook_page}`, '_blank')}
                      title="Facebook"
                    >
                      <Facebook size={14} />
                    </button>
                  )}
                  
                  {vendor.twitter_handle && (
                    <button 
                      className="vendorshop-social-filter-button twitter"
                      onClick={() => window.open(`https://twitter.com/${vendor.twitter_handle}`, '_blank')}
                      title="Twitter"
                    >
                      <Twitter size={14} />
                    </button>
                  )}
                  
                  {vendor.website && (
                    <button 
                      className="vendorshop-social-filter-button website"
                      onClick={() => window.open(vendor.website, '_blank')}
                      title="Website"
                    >
                      <Globe size={14} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="vendorshop-sort-controls-section-main">
                <div className="vendorshop-sort-dropdown-section-main">
                  <button 
                    className="vendorshop-sort-trigger-button-main"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    <Filter size={10} className="vendorshop-sort-icon-main" />
                    <span className="vendorshop-sort-text-main">
                      {sortBy === 'newest' && 'Newest'}
                      {sortBy === 'price_low' && 'Price: Low to High'}
                      {sortBy === 'price_high' && 'Price: High to Low'}
                      {sortBy === 'popular' && 'Most Popular'}
                    </span>
                    {showSortDropdown ? <ChevronUp size={10} className="vendorshop-sort-chevron-main" /> : <ChevronDown size={10} className="vendorshop-sort-chevron-main" />}
                  </button>
                  
                  {showSortDropdown && (
                    <div className="vendorshop-sort-dropdown-menu-main">
                      <button 
                        className={`vendorshop-sort-option-main ${sortBy === 'newest' ? 'vendorshop-sort-option-main-active' : ''}`}
                        onClick={() => { setSortBy('newest'); setShowSortDropdown(false); }}
                      >
                        Newest
                      </button>
                      <button 
                        className={`vendorshop-sort-option-main ${sortBy === 'price_low' ? 'vendorshop-sort-option-main-active' : ''}`}
                        onClick={() => { setSortBy('price_low'); setShowSortDropdown(false); }}
                      >
                        Price: Low to High
                      </button>
                      <button 
                        className={`vendorshop-sort-option-main ${sortBy === 'price_high' ? 'vendorshop-sort-option-main-active' : ''}`}
                        onClick={() => { setSortBy('price_high'); setShowSortDropdown(false); }}
                      >
                        Price: High to Low
                      </button>
                      <button 
                        className={`vendorshop-sort-option-main ${sortBy === 'popular' ? 'vendorshop-sort-option-main-active' : ''}`}
                        onClick={() => { setSortBy('popular'); setShowSortDropdown(false); }}
                      >
                        Most Popular
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="vendorshop-empty-products-section-main">
                <ShoppingBag size={32} className="vendorshop-empty-products-icon-main" />
                <h3 className="vendorshop-empty-products-title-main">No products found</h3>
                <p className="vendorshop-empty-products-message-main">
                  {productFilter !== 'all' ? 'Try clearing the filter' : 'This shop has no products yet'}
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="vendorshop-products-grid-section-main">
                {filteredProducts.map((product) => {
                  const isFavorite = favoritesStatus[product.id] || false;
                  const isHighlighted = initialProductId === product.id;
                  const quantity = getProductQuantity(product.id);
                  const originalImg = product.images && product.images[0] ? product.images[0] : '';
                  const thumbSrc = originalImg ? getThumbnailUrl(originalImg, 400, 65) : '';
                  
                  return (
                    <div 
                      key={product.id} 
                      className={`vendorshop-product-card-main ${isHighlighted ? 'vendorshop-product-card-main-highlighted' : ''}`}
                      onClick={(e) => handleProductClick(product, e)}
                    >
                      <div className="vendorshop-product-image-container-main">
                        {thumbSrc ? (
                          <img 
                            src={thumbSrc} 
                            alt={product.title}
                            className="vendorshop-product-image-main"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (target.dataset.fallback === 'true') return;
                              target.dataset.fallback = 'true';
                              if (originalImg) target.src = getOriginalUrl(originalImg);
                            }}
                          />
                        ) : (
                          <div className="vendorshop-product-image-placeholder-main">
                            <PackageOpen size={20} className="vendorshop-product-image-placeholder-icon" />
                          </div>
                        )}
                        
                        <button 
                          className="vendorshop-product-favorite-button-main"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(product);
                          }}
                        >
                          <Heart 
                            size={14} 
                            className="vendorshop-product-favorite-icon-main"
                            fill={isFavorite ? '#ef4444' : 'none'}
                            stroke={isFavorite ? '#ef4444' : '#6b7280'}
                          />
                        </button>
                        
                        {isHighlighted && (
                          <div className="vendorshop-current-product-badge-main">
                            <Star size={8} /> Viewing
                          </div>
                        )}
                        {product.is_promoted && (
                          <div className="vendorshop-promoted-badge-main">
                            <Zap size={8} /> Promoted
                          </div>
                        )}
                        {product.inventory === 0 && (
                          <div className="vendorshop-out-of-stock-badge-main">
                            ⚠️ Out of Stock
                          </div>
                        )}
                        {product.inventory > 0 && product.inventory < 10 && (
                          <div className="vendorshop-low-stock-badge-main">
                            📦 Low Stock
                          </div>
                        )}
                      </div>
                      
                      <div className="vendorshop-product-info-main">
                        <h3 className="vendorshop-product-title-main">
                          {product.title}
                        </h3>
                        
                        <div className="vendorshop-product-price-row-main">
                          <span className="vendorshop-product-price-main">
                            {formatPrice(product.price, product.currency)}
                          </span>
                          {product.original_price && product.original_price > product.price && (
                            <span className="vendorshop-original-price-main">
                              {formatPrice(product.original_price, product.currency)}
                            </span>
                          )}
                        </div>
                        
                        <div className="vendorshop-product-stats-main">
                          <div className="vendorshop-product-stat-main">
                            <Eye size={8} className="vendorshop-product-stat-icon" />
                            <span className="vendorshop-product-stat-value">{product.views_count || 0}</span>
                          </div>
                          <div className="vendorshop-product-stat-main">
                            <Heart size={8} className="vendorshop-product-stat-icon" />
                            <span className="vendorshop-product-stat-value">{product.likes_count || 0}</span>
                          </div>
                          <div className="vendorshop-product-stat-main">
                            <Package size={8} className="vendorshop-product-stat-icon" />
                            <span className="vendorshop-product-stat-value">{product.inventory}</span>
                          </div>
                        </div>
                        
                        <div className="vendorshop-product-details-main">
                          <span className="vendorshop-condition-badge-main">
                            {product.condition ? product.condition.replace('_', ' ') : 'New'}
                          </span>
                          {product.category && (
                            <span className="vendorshop-category-badge-main">
                              {product.category}
                            </span>
                          )}
                        </div>
                        
                        <div className="vendorshop-product-actions-main">
                          {quantity > 0 ? (
                            <>
                              <button 
                                className="vendorshop-product-action-button-main vendorshop-product-action-button-remove"
                                onClick={(e) => { e.stopPropagation(); removeFromCart(product.id); }}
                              >
                                <Minus size={10} />
                              </button>
                              <span className="vendorshop-product-quantity-badge">{quantity}</span>
                              <button 
                                className="vendorshop-product-action-button-main vendorshop-product-action-button-add"
                                onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                              >
                                <Plus size={10} />
                              </button>
                            </>
                          ) : (
                            <button 
                              className="vendorshop-product-action-button-main vendorshop-product-action-button-add"
                              onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                            >
                              <ShoppingCart size={10} className="vendorshop-product-action-button-icon" />
                              <span className="vendorshop-product-action-button-text">Add</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="vendorshop-products-list-section-main">
                {filteredProducts.map((product) => {
                  const isFavorite = favoritesStatus[product.id] || false;
                  const isHighlighted = initialProductId === product.id;
                  const quantity = getProductQuantity(product.id);
                  const originalImg = product.images && product.images[0] ? product.images[0] : '';
                  const thumbSrc = originalImg ? getThumbnailUrl(originalImg, 200, 65) : '';
                  
                  return (
                    <div 
                      key={product.id} 
                      className={`vendorshop-product-list-item-main ${isHighlighted ? 'vendorshop-product-list-item-main-highlighted' : ''}`}
                      onClick={(e) => handleProductClick(product, e)}
                    >
                      <div className="vendorshop-product-list-image-main">
                        {thumbSrc ? (
                          <img 
                            src={thumbSrc} 
                            alt={product.title}
                            className="vendorshop-product-list-img-main"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (target.dataset.fallback === 'true') return;
                              target.dataset.fallback = 'true';
                              if (originalImg) target.src = getOriginalUrl(originalImg);
                            }}
                          />
                        ) : (
                          <div className="vendorshop-product-list-img-placeholder-main">
                            <PackageOpen size={16} className="vendorshop-product-list-img-placeholder-icon" />
                          </div>
                        )}
                      </div>
                      
                      <div className="vendorshop-product-list-details-main">
                        <h3 className="vendorshop-product-list-title-main">
                          {product.title}
                        </h3>
                        
                        <div className="vendorshop-product-list-price-row-main">
                          <span className="vendorshop-product-list-price-main">
                            {formatPrice(product.price, product.currency)}
                          </span>
                          {product.original_price && product.original_price > product.price && (
                            <span className="vendorshop-product-list-original-price-main">
                              {formatPrice(product.original_price, product.currency)}
                            </span>
                          )}
                        </div>
                        
                        <div className="vendorshop-product-list-stats-main">
                          <div className="vendorshop-product-list-stat-main">
                            <Eye size={8} className="vendorshop-product-list-stat-icon" />
                            <span className="vendorshop-product-list-stat-value">{product.views_count || 0}</span>
                          </div>
                          <div className="vendorshop-product-list-stat-main">
                            <Heart size={8} className="vendorshop-product-list-stat-icon" />
                            <span className="vendorshop-product-list-stat-value">{product.likes_count || 0}</span>
                          </div>
                          <div className="vendorshop-product-list-stat-main">
                            <Package size={8} className="vendorshop-product-list-stat-icon" />
                            <span className="vendorshop-product-list-stat-value">{product.inventory}</span>
                          </div>
                        </div>
                        
                        <div className="vendorshop-product-list-description-main">
                          {product.description?.substring(0, 80)}...
                        </div>
                      </div>
                      
                      <div className="vendorshop-product-list-actions-main">
                        <button 
                          className="vendorshop-product-list-favorite-button-main"
                          onClick={(e) => { e.stopPropagation(); handleToggleFavorite(product); }}
                        >
                          <Heart 
                            size={14} 
                            className="vendorshop-product-list-favorite-icon-main"
                            fill={isFavorite ? '#ef4444' : 'none'}
                            stroke={isFavorite ? '#ef4444' : '#6b7280'}
                          />
                        </button>
                        
                        {quantity > 0 ? (
                          <div className="vendorshop-product-list-quantity-controls">
                            <button 
                              className="vendorshop-product-list-qty-btn"
                              onClick={(e) => { e.stopPropagation(); removeFromCart(product.id); }}
                            >
                              <Minus size={10} />
                            </button>
                            <span className="vendorshop-product-list-qty-badge">{quantity}</span>
                            <button 
                              className="vendorshop-product-list-qty-btn"
                              onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                            >
                              <Plus size={10} />
                            </button>
                          </div>
                        ) : (
                          <button 
                            className={`vendorshop-product-list-action-button-main vendorshop-product-list-action-button-main-add-to-cart`}
                            onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                          >
                            <ShoppingCart size={10} className="vendorshop-product-list-action-button-icon" />
                            <span className="vendorshop-product-list-action-button-text">Add</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="vendorshop-reviews-section-main">
            <div className="vendorshop-reviews-header-section-main">
              <div className="vendorshop-reviews-header-top-row">
                <h2 className="vendorshop-reviews-title-main">Customer Reviews</h2>
              </div>
              
              <div className="vendorshop-reviews-summary-inline">
                <div className="vendorshop-average-rating-inline">
                  <span className="vendorshop-rating-number-inline">{averageRating.toFixed(1)}</span>
                  <div className="vendorshop-rating-stars-inline">
                    {renderStars(averageRating)}
                  </div>
                  <span className="vendorshop-reviews-count-inline">
                    {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                  </span>
                </div>

                <button 
                  className="vendorshop-add-review-button"
                  onClick={handleAddReviewClick}
                >
                  <MessageCircle size={14} /> Comment
                </button>
              </div>

              {showReviewStars && (
                <div className="vendorshop-review-stars-selection">
                  <p className="vendorshop-review-stars-label">Rate your experience</p>
                  <div className="vendorshop-review-stars-row">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        className={`vendorshop-review-star-btn ${selectedRating >= star ? 'selected' : ''}`}
                        onClick={() => handleStarSelect(star)}
                      >
                        <Star size={24} fill={selectedRating >= star ? '#f59e0b' : 'none'} stroke="#f59e0b" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showReviewComment && (
                <div className="vendorshop-review-comment-box">
                  <h4 className="vendorshop-review-comment-title">How can we serve you better?</h4>
                  <textarea
                    className="vendorshop-review-textarea"
                    placeholder="Share your experience with this vendor..."
                    value={reviewComment}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setReviewComment(e.target.value);
                        setReviewError(null);
                      }
                    }}
                    rows={3}
                    maxLength={500}
                  />
                  <div className="vendorshop-review-character-count">
                    {reviewComment.length}/500 characters
                  </div>
                  {reviewError && (
                    <div className="vendorshop-review-error">{reviewError}</div>
                  )}
                  <div className="vendorshop-review-comment-actions">
                    <button 
                      className="vendorshop-review-cancel-btn"
                      onClick={() => {
                        setShowReviewStars(false);
                        setShowReviewComment(false);
                        setSelectedRating(0);
                        setReviewComment('');
                        setReviewError(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="vendorshop-review-submit-btn"
                      onClick={handleSubmitReview}
                      disabled={isSubmittingReview}
                    >
                      {isSubmittingReview ? 'Submitting...' : 'Post Review'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="vendorshop-reviews-list-section-main">
              {reviews.length === 0 ? (
                <div className="vendorshop-no-reviews-section-main">
                  <Star size={32} className="vendorshop-no-reviews-icon-main" />
                  <h3 className="vendorshop-no-reviews-title-main">No reviews yet</h3>
                  <p className="vendorshop-no-reviews-message-main">Be the first to review this vendor!</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="vendorshop-review-card-main">
                    <div className="vendorshop-review-header-section-main">
                      <div className="vendorshop-reviewer-info-section-main">
                        <div className="vendorshop-reviewer-avatar-main">
                          {review.user_name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div className="vendorshop-reviewer-details-section-main">
                          <h4 className="vendorshop-reviewer-name-main">
                            {review.user_name || 'Customer'}
                          </h4>
                          <div className="vendorshop-review-rating-section-main">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                      </div>
                      <span className="vendorshop-review-date-main">
                        {timeAgo(review.created_at)}
                      </span>
                    </div>
                    
                    <p className="vendorshop-review-comment-main">{review.review_text}</p>
                    
                    <div className="vendorshop-review-actions-section-main">
                      <button 
                        className="vendorshop-helpful-button-main"
                        onClick={() => handleHelpfulClick(review.id)}
                      >
                        👍 Helpful ({review.helpful_count || 0})
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="vendorshop-about-section-main">
            <div className="vendorshop-about-content-section-main">
              <div className="vendorshop-about-description-section-main">
                <h3 className="vendorshop-about-title-main">About {vendor.shop_name}</h3>
                <p className="vendorshop-about-text-main">{vendor.description || vendor.bio || 'No description available.'}</p>
              </div>
              
              <div className="vendorshop-shop-details-section-main">
                <h3 className="vendorshop-shop-details-title-main">Shop Details</h3>
                <div className="vendorshop-details-grid-section-main">
                  <div className="vendorshop-detail-item-section-main">
                    <Calendar size={10} className="vendorshop-detail-icon-main" />
                    <div className="vendorshop-detail-content-section-main">
                      <div className="vendorshop-detail-label-main">Member Since</div>
                      <div className="vendorshop-detail-value-main">
                        {new Date(vendor.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="vendorshop-detail-item-section-main">
                    <MessageCircle size={10} className="vendorshop-detail-icon-main" />
                    <div className="vendorshop-detail-content-section-main">
                      <div className="vendorshop-detail-label-main">Response Rate</div>
                      <div className="vendorshop-detail-value-main">{stats?.response_rate}%</div>
                    </div>
                  </div>
                  
                  <div className="vendorshop-detail-item-section-main">
                    <ShoppingBag size={10} className="vendorshop-detail-icon-main" />
                    <div className="vendorshop-detail-content-section-main">
                      <div className="vendorshop-detail-label-main">Total Products</div>
                      <div className="vendorshop-detail-value-main">{stats?.total_products || vendor.total_products || 0}</div>
                    </div>
                  </div>
                  
                  <div className="vendorshop-detail-item-section-main">
                    <Eye size={10} className="vendorshop-detail-icon-main" />
                    <div className="vendorshop-detail-content-section-main">
                      <div className="vendorshop-detail-label-main">Total Visits</div>
                      <div className="vendorshop-detail-value-main">{totalViews}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="vendorshop-contact-info-section-main">
                <h3 className="vendorshop-contact-info-title-main">Contact Information</h3>
                <div className="vendorshop-contact-details-section-main">
                  {vendor.contact_email && (
                    <div className="vendorshop-contact-detail-section-main">
                      <Mail size={10} className="vendorshop-contact-detail-icon" />
                      <span className="vendorshop-contact-detail-text">{vendor.contact_email}</span>
                    </div>
                  )}
                  
                  {vendor.business_email && !vendor.contact_email && (
                    <div className="vendorshop-contact-detail-section-main">
                      <Mail size={10} className="vendorshop-contact-detail-icon" />
                      <span className="vendorshop-contact-detail-text">{vendor.business_email}</span>
                    </div>
                  )}
                  
                  {vendor.contact_phone && (
                    <div className="vendorshop-contact-detail-section-main">
                      <Phone size={10} className="vendorshop-contact-detail-icon" />
                      <span className="vendorshop-contact-detail-text">{vendor.contact_phone}</span>
                    </div>
                  )}
                  
                  {vendor.location && (
                    <div className="vendorshop-contact-detail-section-main">
                      <MapPin size={10} className="vendorshop-contact-detail-icon" />
                      <span className="vendorshop-contact-detail-text">{vendor.location}</span>
                    </div>
                  )}
                  
                  {vendor.website && (
                    <div className="vendorshop-contact-detail-section-main">
                      <Globe size={10} className="vendorshop-contact-detail-icon" />
                      <a 
                        href={vendor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendorshop-contact-detail-link"
                      >
                        {vendor.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {vendor.business_hours && (
                <div className="vendorshop-business-hours-section-main">
                  <h3 className="vendorshop-business-hours-title-main">
                    <Clock size={10} className="vendorshop-business-hours-icon" />
                    Business Hours
                  </h3>
                  <pre className="vendorshop-business-hours-text-main">{vendor.business_hours}</pre>
                </div>
              )}

              {vendor.instagram_handle || vendor.facebook_page || vendor.twitter_handle ? (
                <div className="vendorshop-social-section-main">
                  <h3 className="vendorshop-social-title-main">Connect With Us</h3>
                  <div className="vendorshop-social-links-section-main">
                    {vendor.instagram_handle && (
                      <a 
                        href={`https://instagram.com/${vendor.instagram_handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendorshop-social-link-main vendorshop-instagram-link-main"
                      >
                        <Instagram size={10} className="vendorshop-social-link-icon" />
                        <span className="vendorshop-social-link-text">Instagram</span>
                      </a>
                    )}
                    
                    {vendor.facebook_page && (
                      <a 
                        href={`https://facebook.com/${vendor.facebook_page}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendorshop-social-link-main vendorshop-facebook-link-main"
                      >
                        <Facebook size={10} className="vendorshop-social-link-icon" />
                        <span className="vendorshop-social-link-text">Facebook</span>
                      </a>
                    )}
                    
                    {vendor.twitter_handle && (
                      <a 
                        href={`https://twitter.com/${vendor.twitter_handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendorshop-social-link-main vendorshop-twitter-link-main"
                      >
                        <Twitter size={10} className="vendorshop-social-link-icon" />
                        <span className="vendorshop-social-link-text">Twitter</span>
                      </a>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {activeTab === 'policies' && (
          <div className="vendorshop-policies-section-main">
            <div className="vendorshop-policies-content-section-main">
              <div className="vendorshop-policy-card-main">
                <h3 className="vendorshop-policy-title-main">
                  <Shield size={10} className="vendorshop-policy-icon" />
                  Payment
                </h3>
                <p className={vendor.return_policy ? "vendorshop-policy-text-main" : "vendorshop-no-policy-text-main"}>
                  {vendor.return_policy || 'No payment policy specified by this vendor.'}
                </p>
              </div>
              
              <div className="vendorshop-policy-card-main">
                <h3 className="vendorshop-policy-title-main">
                  <Truck size={10} className="vendorshop-policy-icon" />
                  Delivery
                </h3>
                <p className={vendor.shipping_policy ? "vendorshop-policy-text-main" : "vendorshop-no-policy-text-main"}>
                  {vendor.shipping_policy || 'No delivery policy specified by this vendor.'}
                </p>
              </div>

              {/* =====================================================
                  LINK VIEWER — website or social in iframe
                  ===================================================== */}
              {shopLinks.length > 0 && currentLink ? (
                <div
                  className={`vendorshop-link-frame-main ${
                    isLinkFullscreen
                      ? 'vendorshop-link-frame-fullscreen'
                      : 'vendorshop-link-frame-inline'
                  }`}
                  style={
                    isLinkFullscreen
                      ? { position: 'fixed', inset: 0, zIndex: 999999, margin: 0, borderRadius: 0 }
                      : { position: 'relative', zIndex: 1 }
                  }
                >
                  {/* Header with controls */}
                  <div className="vendorshop-link-frame-header-main">
                    {showLinkControls ? (
                      <>
                        <div className="vendorshop-link-frame-label-main">
                          {currentLink.icon}
                          <span>
                            {currentLink.label} ({currentLinkIndex + 1}/{shopLinks.length})
                          </span>
                        </div>

                        <div className="vendorshop-link-frame-controls-main">
                          {/* Fullscreen / Minimize toggle */}
                          <button
                            className="vendorshop-link-control-btn-main"
                            onClick={handleToggleFullscreen}
                            onMouseEnter={() => setHoverFullscreen(true)}
                            onMouseLeave={() => setHoverFullscreen(false)}
                            aria-label={isLinkFullscreen ? 'Minimize' : 'Expand full screen'}
                          >
                            {isLinkFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                            {hoverFullscreen && (
                              <span className="vendorshop-link-control-tooltip">
                                {isLinkFullscreen ? 'Minimize' : 'Expand full screen'}
                              </span>
                            )}
                          </button>

                          {/* Switch link (only if more than one) */}
                          {hasMultipleLinks && (
                            <button
                              className="vendorshop-link-control-btn-main"
                              onClick={handleSwitchLink}
                              aria-label="Switch link"
                            >
                              <Repeat size={12} />
                              <span className="vendorshop-link-control-tooltip">Switch link</span>
                            </button>
                          )}

                          {/* Refresh */}
                          <button
                            className="vendorshop-link-control-btn-main"
                            onClick={handleRefreshLink}
                            aria-label="Reload"
                          >
                            <RefreshCw size={12} />
                            <span className="vendorshop-link-control-tooltip">Reload</span>
                          </button>

                          {/* Open in browser */}
                          <button
                            className="vendorshop-link-control-btn-main"
                            onClick={handleOpenInBrowser}
                            aria-label="Open in browser"
                          >
                            <ExternalLink size={12} />
                            <span className="vendorshop-link-control-tooltip">Open in browser</span>
                          </button>

                          {/* Hide controls */}
                          <button
                            className="vendorshop-link-control-btn-main"
                            onClick={handleHideControls}
                            aria-label="Hide buttons"
                          >
                            <EyeOff size={12} />
                            <span className="vendorshop-link-control-tooltip">Hide buttons</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        className="vendorshop-link-unhide-btn-main"
                        onClick={handleShowControls}
                        aria-label="Show buttons"
                      >
                        <EyeIcon size={12} />
                        <span>Show</span>
                      </button>
                    )}
                  </div>

                  {/* Iframe body — FORCED to render inline */}
                  <div className="vendorshop-link-frame-body-main">
                    <iframe
                      ref={iframeRef}
                      key={`${currentLink.key}-${iframeKey}`}
                      src={currentLink.url}
                      title={currentLink.label}
                      className="vendorshop-link-iframe-main"
                      referrerPolicy="no-referrer-when-downgrade"
                      allow="clipboard-read; clipboard-write; fullscreen; geolocation; microphone; camera"
                      allowFullScreen
                      loading="eager"
                      onLoad={() => {
                        console.log('[VendorShop] iframe loaded:', currentLink.url);
                      }}
                      onError={() => {
                        console.warn('[VendorShop] iframe failed:', currentLink.url);
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 0,
                        display: 'block',
                        background: '#ffffff'
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="vendorshop-link-empty-state-main">
                  <Globe size={20} />
                  <div className="vendorshop-link-empty-title-main">No links available</div>
                  <div className="vendorshop-link-empty-text-main">
                    This vendor hasn't added a website or social media link yet.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {!embedded && (
        <footer className="vendorshop-footer-section-main">
          <div className="vendorshop-footer-content-section-main">
            <p className="vendorshop-footer-text-main">© {new Date().getFullYear()} GOSTOREz Enterprise</p>
            <p className="vendorshop-footer-subtext-main">All rights reserved</p>
          </div>
        </footer>
      )}
      
      {localSelectedProduct && (
        <div style={{ position: 'fixed', zIndex: 1003, top: 0, left: 0, right: 0, bottom: 0 }}>
          <ProductDisplay
            product={localSelectedProduct}
            vendor={vendor}
            currentImageIndex={0}
            onClose={() => setLocalSelectedProduct(null)}
            onNavigateToVendorShop={() => setLocalSelectedProduct(null)}
            embedded={true}
          />
          <button
            onClick={() => setLocalSelectedProduct(null)}
            style={{
              position: 'fixed',
              top: '10px',
              right: '10px',
              zIndex: 2000,
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)'
            }}
          >
            <X size={20} />
          </button>
        </div>
      )}
      
      <div className="vendorshop-debug-info-section-main">
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">Vendor ID:</span>
          <span className="vendorshop-debug-value-main">{vendorId}</span>
        </div>
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">Products:</span>
          <span className="vendorshop-debug-value-main">{products.length}</span>
        </div>
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">Reviews:</span>
          <span className="vendorshop-debug-value-main">{reviews.length}</span>
        </div>
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">Active Tab:</span>
          <span className="vendorshop-debug-value-main">{activeTab}</span>
        </div>
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">View Mode:</span>
          <span className="vendorshop-debug-value-main">{viewMode}</span>
        </div>
        <div className="vendorshop-debug-item-main">
          <span className="vendorshop-debug-label-main">Shop Links:</span>
          <span className="vendorshop-debug-value-main">{shopLinks.length}</span>
        </div>
      </div>
    </div>
  );
};

export default VendorShop;